import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import type { TermsInfo } from "@/api/types";
import { apiErrorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

/**
 * 初回ログイン時や規約改訂時に表示される同意画面。
 * 最後までスクロールして「同意する」ボタンを押すまで先に進めない。
 */
export function TermsAcceptancePage() {
  const { refreshMe, logout, user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<TermsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await endpoints.fetchTerms();
        if (!cancelled) setInfo(res);
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, "利用規約の取得に失敗しました"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // 最下部から 20px 以内まで来たら OK
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setScrolledToBottom(true);
    }
  };

  const onAccept = async () => {
    if (!info) return;
    setSubmitting(true);
    setError(null);
    try {
      await endpoints.acceptTerms(info.version);
      await refreshMe();
      const dest = user?.role === "admin" ? "/admin/orders" : "/catalog";
      navigate(dest, { replace: true });
    } catch (e) {
      setError(apiErrorMessage(e, "同意処理に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        読み込み中...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl card p-6 flex flex-col max-h-[90vh]">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">ご利用前に利用規約の確認をお願いします</h1>
          <p className="text-sm text-gray-600 mt-1">
            本サービスをご利用いただくには、下記の利用規約への同意が必要です。
            最後までお読みいただき、「同意する」にチェックを入れてお進みください。
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 mb-3">
            {error}
          </div>
        )}

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto border rounded-md p-4 bg-white prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-6 min-h-0"
        >
          {info?.body || "（規約本文が取得できませんでした）"}
        </div>

        {!scrolledToBottom && (
          <div className="mt-2 text-xs text-gray-500 text-center">
            ↓ 最後までスクロールしてください
          </div>
        )}

        <div className="mt-4 border-t pt-4 space-y-3">
          <label
            className={`flex items-start gap-2 cursor-pointer ${
              !scrolledToBottom ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={!scrolledToBottom || submitting}
              className="mt-1"
            />
            <span className="text-sm">
              利用規約（バージョン {info?.version}）の内容を確認し、これに同意します。
            </span>
          </label>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancel}
              disabled={submitting}
            >
              ログアウト
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onAccept}
              disabled={!scrolledToBottom || !checked || submitting}
            >
              {submitting ? "処理中..." : "同意して進む"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
